// DeveloperDashboard.jsx - Developer dashboard with keys, requests, and usage stats
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDeveloperAuth } from '../../contexts/DeveloperAuthContext';
import RequestKeyForm from './RequestKeyForm';
import KeyDisplay from './KeyDisplay';

function DeveloperDashboard() {
  const navigate = useNavigate();
  const { user, profile, signOut, needsProfile, updateProfile, authFetch, loading: authLoading } = useDeveloperAuth();

  const [activeTab, setActiveTab] = useState('overview');
  const [keys, setKeys] = useState([]);
  const [requests, setRequests] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    organization: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  // Load data on mount
  useEffect(() => {
    if (!authLoading && user && profile) {
      loadData();
    }
  }, [authLoading, user, profile]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load keys and requests
      const keysResponse = await authFetch('/api/developer/my-keys');
      if (keysResponse.ok) {
        const keysData = await keysResponse.json();
        setKeys(keysData.keys || []);
        setRequests(keysData.requests || []);
      }

      // Load usage stats
      const usageResponse = await authFetch('/api/developer/usage');
      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        setUsage(usageData);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileError(null);

    const { error } = await updateProfile(profileForm.name, profileForm.organization);
    if (error) {
      setProfileError(error.message || 'Failed to save profile');
    }
    setProfileLoading(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/developers/login');
  };

  // Show profile setup if needed
  if (!authLoading && needsProfile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header user={user} onSignOut={handleSignOut} />
        <div className="max-w-xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Complete Your Profile</h2>
            <p className="text-gray-600 mb-6">Tell us about yourself to get started</p>

            <form onSubmit={handleProfileSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder="John Doe"
                  required
                  minLength={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization (Optional)
                </label>
                <input
                  type="text"
                  value={profileForm.organization}
                  onChange={(e) => setProfileForm({ ...profileForm, organization: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder="Company or organization name"
                />
              </div>

              {profileError && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-600">{profileError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={profileLoading}
                className="w-full py-3 px-4 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 disabled:opacity-50"
              >
                {profileLoading ? 'Saving...' : 'Continue'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const hasPendingRequest = pendingRequests.length > 0;
  const activeKeyCount = keys.filter(k => k.is_active).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} profile={profile} onSignOut={handleSignOut} />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-5">
          <h2 className="text-base font-semibold text-gray-900">Integration Lifecycle</h2>
          <p className="mt-1 text-sm text-gray-600">
            Request access, wait for approval, reveal your key, then integrate against the v2 contract.
            Rate limits are applied per key and visible in your API keys tab.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Access Requests</p>
              <p className="text-xl font-semibold text-gray-900">{requests.length}</p>
            </div>
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Pending Review</p>
              <p className="text-xl font-semibold text-gray-900">{pendingRequests.length}</p>
            </div>
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Active Keys</p>
              <p className="text-xl font-semibold text-gray-900">{activeKeyCount}</p>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 bg-gray-100 rounded-xl p-1 mb-8 max-w-md">
          {['overview', 'keys', 'request'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab === 'overview' ? 'Overview' : tab === 'keys' ? 'API Keys' : 'Request Key'}
            </button>
          ))}
        </div>

        {/* Error State */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl mb-6">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && activeTab !== 'request' ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatCard
                    label="Total API Calls"
                    value={usage?.totalUsage?.toLocaleString() || '0'}
                    icon={<ActivityIcon />}
                  />
                  <StatCard
                    label="This Month"
                    value={usage?.monthUsage?.toLocaleString() || '0'}
                    icon={<CalendarIcon />}
                  />
                  <StatCard
                    label="Active Keys"
                    value={usage?.activeKeys || keys.filter(k => k.is_active).length || 0}
                    icon={<KeyIcon />}
                  />
                </div>

                {/* Recent Activity */}
                {usage?.recentActivity?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Recent Activity (7 days)</h3>
                    <div className="space-y-3">
                      {usage.recentActivity.slice(0, 5).map((day, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">
                            {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          <div className="flex items-center space-x-4">
                            <span className="text-gray-900 font-medium">{day.requests} requests</span>
                            <span className="text-green-600">{day.success_count} success</span>
                            {day.error_count > 0 && (
                              <span className="text-red-600">{day.error_count} errors</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link
                    to="/api-docs"
                    className="bg-white rounded-2xl border border-gray-200 p-6 hover:border-gray-300 transition-all group"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-all">
                        <DocumentIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">API Documentation</h3>
                        <p className="text-sm text-gray-600 mt-1">Contract schema, error codes, and code samples</p>
                      </div>
                    </div>
                  </Link>

                  <Link
                    to="/playground"
                    className="bg-white rounded-2xl border border-gray-200 p-6 hover:border-gray-300 transition-all group"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-all">
                        <PlayIcon className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">API Playground</h3>
                        <p className="text-sm text-gray-600 mt-1">Validate payloads and preview annotated image output</p>
                      </div>
                    </div>
                  </Link>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Integration Checklist</h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>1. Keep at least one active key in the API Keys tab.</p>
                    <p>2. Integrate <code>/api/v2/skin-analysis</code> with <code>MySkinAI-API-Key</code>.</p>
                    <p>3. Handle <code>error.code</code> responses and read output from <code>data</code> and <code>annotatedImage</code>.</p>
                    <p>4. Use <code>includeAnnotatedImage=false</code> when testing low-payload paths.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Keys Tab */}
            {activeTab === 'keys' && (
              <KeyDisplay keys={keys} requests={requests} onRefresh={loadData} />
            )}

            {/* Request Tab */}
            {activeTab === 'request' && (
              <RequestKeyForm
                hasPendingRequest={hasPendingRequest}
                onSuccess={() => {
                  loadData();
                  setActiveTab('keys');
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Header Component
function Header({ user, profile, onSignOut }) {
  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">MySkinAI</h1>
              <p className="text-xs text-gray-500">Developer Portal</p>
            </div>
          </Link>

          <div className="flex items-center space-x-4">
            <Link
              to="/api-docs"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Docs
            </Link>
            <Link
              to="/playground"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Playground
            </Link>
            <div className="flex items-center space-x-3 pl-4 border-l border-gray-200">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{profile?.name || user?.email}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <button
                onClick={onSignOut}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <LogoutIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

// Stat Card Component
function StatCard({ label, value, icon }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
          {icon}
        </div>
      </div>
    </div>
  );
}

// Icons
const ActivityIcon = () => (
  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const KeyIcon = () => (
  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
  </svg>
);

const DocumentIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const PlayIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LogoutIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

export default DeveloperDashboard;
